import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import {
  useEmissionsAvailableYears,
  useEmissionsBySource,
  useEmissionsPcf,
} from "../hooks/useInsightsDashboardQueries";

const EMISSIONS_COLORS = [
  "#509EE3",
  "#88BF4D",
  "#A989C5",
  "#EF8C8C",
  "#F9D45C",
  "#F2A86F",
  "#98D9D9",
  "#7172AD",
  "#6A7ECC",
  "#ED6236",
];

type SourceInfo = {
  onFarm: string;
  profitability: string;
};

const SOURCE_CONTEXT: Record<string, SourceInfo> = {
  "enteric fermentation": {
    onFarm:
      "Methane produced during digestion. Driven by herd size, feed quality and animal genetics. Better feed efficiency means animals convert more feed into growth and less into methane.",
    profitability:
      "Improving feed conversion directly reduces feed costs per kg of liveweight gained — your biggest variable cost.",
  },
  "manure management": {
    onFarm:
      "Greenhouse gases released from slurry and manure during storage and spreading. Affected by storage type, how long it's stored, and when and how it's applied.",
    profitability:
      "Better manure handling captures more nitrogen and phosphorus value, reducing the amount of bought fertiliser you need.",
  },
  "fertiliser use": {
    onFarm:
      "Nitrous oxide released when synthetic nitrogen is applied to soil. Driven by application rates, timing, and whether it matches crop demand.",
    profitability:
      "Precision application avoids wasting expensive fertiliser. Every kg of N you don't need to buy is money saved.",
  },
  fuel: {
    onFarm:
      "CO₂ from diesel and petrol used in machinery, vehicles and field operations. Driven by the number of passes, machinery efficiency and travel distances.",
    profitability:
      "Less fuel burned means direct cost savings. Reducing tillage passes or optimising routes cuts both emissions and your fuel bill.",
  },
  "purchased feed": {
    onFarm:
      "Emissions from producing and transporting bought-in feed concentrates and supplements. The more you rely on external feed, the higher this source.",
    profitability:
      "Growing more quality forage on-farm reduces reliance on expensive bought feed — often the second-largest cost after labour.",
  },
  electricity: {
    onFarm:
      "Energy used for milking, cooling, water heating, lighting and farm buildings. Driven by equipment age and usage patterns.",
    profitability:
      "Energy-efficient equipment and routines lower your utility bills month after month.",
  },
  lime: {
    onFarm:
      "CO₂ released when lime breaks down in acidic soils. Application rates depend on soil pH and target levels.",
    profitability:
      "Correct liming unlocks soil nutrients already present, meaning your fertiliser and grass grow more efficiently.",
  },
  bedding: {
    onFarm:
      "Emissions from producing and transporting bedding materials like straw or wood chip.",
    profitability:
      "Sourcing bedding locally or using alternative materials can reduce both transport costs and emissions.",
  },
};

const DEFAULT_CONTEXT: SourceInfo = {
  onFarm: "Emissions from miscellaneous on-farm activities and inputs.",
  profitability:
    "Reducing any emission source typically points to a resource being used more efficiently, which saves money.",
};

function getSourceContext(name: string): SourceInfo {
  const key = name.toLowerCase().trim();
  return SOURCE_CONTEXT[key] ?? DEFAULT_CONTEXT;
}

export function CarbonFootprintTab({ farmerGuid }: { farmerGuid?: string }) {
  const { data: yearsData } = useEmissionsAvailableYears(farmerGuid);
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    for (const row of yearsData?.data ?? []) {
      const y = row["Emissions.year"];
      if (y != null) years.add(Number(y));
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [yearsData?.data]);

  const [selectedYear, setSelectedYear] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (!availableYears.length) return;
    if (!selectedYear || !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  const { data: pcfData, isLoading: pcfLoading } = useEmissionsPcf(
    farmerGuid,
    selectedYear
  );
  const { data: sourceData, isLoading: sourceLoading } = useEmissionsBySource(
    farmerGuid,
    selectedYear
  );

  const pcfValue = pcfData?.data?.[0]?.["Emissions.avgValue"];
  const formattedPcf =
    pcfValue != null ? Number(pcfValue).toFixed(1) : undefined;

  const sources = (sourceData?.data ?? [])
    .map((r) => ({
      name: r["Emissions.parameter"],
      value: Number(r["Emissions.avgValue"]),
    }))
    .filter((s) => s.value > 0);

  const total = sources.reduce((sum, s) => sum + s.value, 0);

  const isLoading = pcfLoading || sourceLoading;
  const hasData = pcfValue != null || sources.length > 0;

  if (!isLoading && !hasData) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto" }}>
        <Card>
          <Typography variant="body2" sx={{ color: "#71717a" }}>
            No emissions data available yet. Your carbon footprint breakdown
            will appear here once your programme data has been processed.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>
      {availableYears.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <TextField
            select
            size="small"
            label="Year"
            value={selectedYear ?? ""}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            sx={{ minWidth: 100 }}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}
      {/* Overview */}
      <Card>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#18181b", mb: 1 }}
        >
          Carbon Footprint
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: "#52525b", lineHeight: 1.7 }}
            >
              Your PCF measures the total greenhouse gases produced per kilogram
              of product. Below is a breakdown of where your emissions come
              from, what drives each source, and how managing them can improve
              your margins — not just your environmental impact.
            </Typography>
          </Box>

          {isLoading ? (
            <Box sx={{ textAlign: "center", px: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                flexShrink: 0,
                minWidth: 120,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#71717a", fontWeight: 500 }}
              >
                Your PCF
              </Typography>
              <Typography
                sx={{
                  fontSize: "2.25rem",
                  fontWeight: 700,
                  color: "#18181b",
                  lineHeight: 1,
                  my: 0.5,
                }}
              >
                {formattedPcf ?? "-"}
              </Typography>
              <Typography variant="caption" sx={{ color: "#71717a" }}>
                kg CO₂e / kg
              </Typography>
            </Box>
          )}
        </Box>
      </Card>

      {/* Emission Sources */}
      {isLoading ? (
        <Card>
          <Box sx={{ textAlign: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        </Card>
      ) : (
        sources.map((source, index) => (
          <SourceCard
            key={source.name}
            source={source}
            index={index}
            total={total}
            pcf={pcfValue != null ? Number(pcfValue) : undefined}
          />
        ))
      )}
    </Box>
  );
}

function SourceCard({
  source,
  index,
  total,
  pcf,
}: {
  source: { name: string; value: number };
  index: number;
  total: number;
  pcf?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const pct = total > 0 ? (source.value / total) * 100 : 0;
  const color = EMISSIONS_COLORS[index % EMISSIONS_COLORS.length];
  const context = getSourceContext(source.name);
  const kgValue = pcf != null ? source.value * pcf : undefined;

  return (
    <SourceCardWrapper onClick={() => setExpanded((prev) => !prev)}>
      {/* Source header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: color,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: "#18181b",
              textTransform: "capitalize",
            }}
          >
            {source.name}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: "#18181b" }}
          >
            {pct.toFixed(1)}%
          </Typography>
          <IconButton
            size="small"
            sx={{
              p: 0.25,
              transition: "transform 0.2s",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: 18, color: "#a1a1aa" }} />
          </IconButton>
        </Box>
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: "#f4f4f5",
          "& .MuiLinearProgress-bar": {
            backgroundColor: color,
            borderRadius: 3,
          },
        }}
      />

      {/* Collapsible detail */}
      <Collapse in={expanded}>
        <Box sx={{ mt: 1.5 }}>
          <Typography
            variant="caption"
            sx={{ color: "#a1a1aa", display: "block", mb: 1.5 }}
          >
            {kgValue != null ? kgValue.toFixed(2) : source.value.toFixed(2)} kg
            CO₂e/kg
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <ContextBlock>
              <ContextLabel>What drives this on your farm</ContextLabel>
              <ContextText>{context.onFarm}</ContextText>
            </ContextBlock>
            <ContextBlock>
              <ContextLabel>Why it matters for your margins</ContextLabel>
              <ContextText>{context.profitability}</ContextText>
            </ContextBlock>
          </Box>
        </Box>
      </Collapse>
    </SourceCardWrapper>
  );
}

const SourceCardWrapper = styled("div")({
  backgroundColor: "white",
  borderRadius: 16,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
  marginBottom: 16,
  cursor: "pointer",
  "&:hover": {
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.12)",
  },
});

const Card = styled("div")({
  backgroundColor: "white",
  borderRadius: 16,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
  marginBottom: 16,
});

const ContextBlock = styled("div")({
  padding: "10px 12px",
  backgroundColor: "#f9faf6",
  borderRadius: 10,
  borderLeft: "3px solid #c8dbb0",
});

const ContextLabel = styled(Typography)({
  fontSize: "0.7rem",
  fontWeight: 600,
  color: "#71717a",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  marginBottom: 4,
});

const ContextText = styled(Typography)({
  fontSize: "0.825rem",
  color: "#3f3f46",
  lineHeight: 1.6,
});
